import { pathOr } from "ramda";

import { theme } from "@sisuwellness/web-components";
import {
    isMobileView,
    createLegends,
    calculateMinMax,
    createYGridValues,
    flattenPointValues,
    createPlottableDataPoints,
    calculateMeanValue,
    downBy
} from "components/Templates/TrendChart/Line/utils";
import { METRIC_INDICATOR } from "utilities/metrics/mapIndicators";
import calculatePointColor from "utilities/trend-chart/calculateGuldeline";
import { BODY_MASS_INDEX } from "constants/trend-charts";
import { fixedNumber } from "utilities/commonUtils";
import i18n from "../../../../i18n.js";

const defaultColor = theme.portalColor.hpPrimaryPurple.hex;

const METRIC_FIELDS = {
    rounded: false,
    key: ["bmi"],
    // `isPregnant` must survive the datum `pick()` because the line layer reads it per point to
    // dash segments spanning a pregnancy. Ethnicity does not need to: the rating evaluator runs
    // on the full health check before the pick, so the band is already resolved by then.
    additionalKeys: ["height", "weight", "rating", "unit", "nhsEthnicity", "isPregnant"]
};

/**
 * Update data with rating
 * @param {object} data
 * @return {{ rating: { key: string, label: string, ranges: object } }}
 */
const bmiRatingCalc = data => {
    // If bmi is null, check if weight and height is non-null (To Calcluate BMI)
    if (!data.bmi && !(data.weight && data.height)) return { bmi: null };
    const { guideline, currentValue } = calculatePointColor(BODY_MASS_INDEX, data);

    return {
        ...data,
        unit: "BMI",
        bmi: fixedNumber(currentValue),
        // `guideline.ranges` is the instance getter (ethnicity-aware). The static
        // `constructor.ranges` carries the un-adjusted NICE 1.9.10 literals, so using it here
        // printed standard band labels in the legend for adjusted-threshold members.
        rating: { key: guideline?.key, label: guideline?.label, ranges: guideline?.ranges }
    };
};

const plot = {
    unit: () => `${i18n.t("words.body_mass_index")} (${i18n.t("units.uc_bmi")})`,

    color: ({ rating } = {}) => theme.guidelines.bodyMassIndex[(rating?.key)]?.sisuPortal?.hex ?? defaultColor,

    dataPoints: (healthChecks = [], settings) => {
        const points = createPlottableDataPoints(data => bmiRatingCalc(data, settings))(
            healthChecks,
            METRIC_FIELDS.key,
            METRIC_FIELDS.additionalKeys
        );

        return points;
    },

    yRange: (points = []) => {
        const { minimum, maximum } = calculateMinMax(flattenPointValues(points, "y"));
        const minValue = downBy(minimum);
        const ranges = createYGridValues(minValue, maximum, { parts: 5, rounded: METRIC_FIELDS.rounded }).map(num =>
            num.toFixed(1)
        );

        return {
            min: Number(ranges[0]),
            yScaleRanges: ranges,
            max: Number(ranges[ranges.length - 1])
        };
    },

    legends: (points = []) =>
        createLegends(
            pathOr({}, [0, "data", 0, "rating", "ranges"], points),
            METRIC_INDICATOR.BODY_MASS_INDEX.themeKey,
            "words.body_mass_index"
        ),

    historyProps: (filteredPoints = [], latestHealthCheckData = {}) => {
        const { weight, height, bmi: bodyMassIndex, nhsEthnicity, ireEthnicity } = latestHealthCheckData; // bmi can be null
        const { rating, bmi } = bmiRatingCalc({ weight, height, bmi: bodyMassIndex, nhsEthnicity, ireEthnicity });
        const meanBmi = calculateMeanValue("bmi", filteredPoints[0]);

        const { rating: meanRating } = bmiRatingCalc({ bmi: meanBmi });

        return {
            unit: i18n.t("units.uc_bmi"),
            value: fixedNumber(bmi),
            meanValue: fixedNumber(meanBmi),
            valueColor: theme.guidelines.bodyMassIndex[(rating?.key)]?.sisuPortal?.hex,
            meanColor: theme.guidelines.bodyMassIndex[(meanRating?.key)]?.sisuPortal?.hex
        };
    },

    margins: width => ({ right: isMobileView(width) ? 16 : 80, left: isMobileView(width) ? 50 : 80 })
};

export default { plottingUtility: plot };
